import React, { useState } from 'react';
import { Check, Plus, Trash2, Eye, Save } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface Zielerreichung {
  ziel: string;
  erreichungsgrad: number;
  kommentar: string;
}

interface Transferaktion {
  was: string;
  wann: string;
  wie: string;
}

interface AbschlussData {
  zielerreichung: Zielerreichung[];
  zusammenfassung: string;
  key_learnings: string;
  transferplan: Transferaktion[];
  rueckfallpraevention: string;
  empfehlungen: string;
  gesamtbewertung: number;
  abschluss_kommentar: string;
}

const defaultData: AbschlussData = {
  zielerreichung: [],
  zusammenfassung: '',
  key_learnings: '',
  transferplan: [],
  rueckfallpraevention: '',
  empfehlungen: '',
  gesamtbewertung: 5,
  abschluss_kommentar: '',
};

interface Props {
  data: any;
  onSave: (data: any) => Promise<void>;
  blockId: string;
  clientEmail: string;
}

export function AbschlussForm({ data, onSave, blockId, clientEmail }: Props) {
  const { showToast } = useToast();
  const [form, setForm] = useState<AbschlussData>({ ...defaultData, ...data });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'range') {
      setForm({ ...form, [name]: Number(value) });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  // Zielerreichung
  const addZielerreichung = () => {
    setForm({ ...form, zielerreichung: [...form.zielerreichung, { ziel: '', erreichungsgrad: 50, kommentar: '' }] });
  };
  const updateZielerreichung = (index: number, field: keyof Zielerreichung, value: string | number) => {
    const updated = [...form.zielerreichung];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, zielerreichung: updated });
  };
  const removeZielerreichung = (index: number) => {
    setForm({ ...form, zielerreichung: form.zielerreichung.filter((_, i) => i !== index) });
  };

  // Transferplan
  const addTransfer = () => {
    setForm({ ...form, transferplan: [...form.transferplan, { was: '', wann: '', wie: '' }] });
  };
  const updateTransfer = (index: number, field: keyof Transferaktion, value: string) => {
    const updated = [...form.transferplan];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, transferplan: updated });
  };
  const removeTransfer = (index: number) => {
    setForm({ ...form, transferplan: form.transferplan.filter((_, i) => i !== index) });
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/blocks/${blockId}/export/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Abschluss.pdf';
      a.click();
      URL.revokeObjectURL(url);
      showToast('PDF heruntergeladen', 'success');
    } catch {
      showToast('Export fehlgeschlagen', 'error');
    }
  };

  return (
    <div className="block-form">
      {/* Zielerreichung */}
      <div className="block-form-section">
        <h4>Zielerreichung</h4>
        {form.zielerreichung.map((z, i) => (
          <div className="dynamic-list-item" key={i} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <input
                type="text"
                value={z.ziel}
                onChange={(e) => updateZielerreichung(i, 'ziel', e.target.value)}
                placeholder="Ziel..."
                style={{ flex: 1 }}
              />
              <button className="btn-icon" onClick={() => removeZielerreichung(i)} title="Entfernen">
                <Trash2 size={14} />
              </button>
            </div>
            <div className="percentage-display" style={{ marginTop: 8 }}>
              <div className="percentage-bar">
                <div className="percentage-fill" style={{ width: `${z.erreichungsgrad}%` }} />
              </div>
              <span className="percentage-value">{z.erreichungsgrad}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={z.erreichungsgrad}
              onChange={(e) => updateZielerreichung(i, 'erreichungsgrad', Number(e.target.value))}
              style={{ width: '100%', marginTop: 4 }}
            />
            <input
              type="text"
              value={z.kommentar}
              onChange={(e) => updateZielerreichung(i, 'kommentar', e.target.value)}
              placeholder="Kommentar..."
              style={{ marginTop: 4 }}
            />
          </div>
        ))}
        <button className="btn btn-sm btn-secondary" onClick={addZielerreichung}>
          <Plus size={14} /> Ziel hinzufügen
        </button>
      </div>

      {/* Zusammenfassung */}
      <div className="block-form-section">
        <h4>Zusammenfassung</h4>
        <div className="form-group">
          <label>Gesamtzusammenfassung des Coaching-Prozesses</label>
          <textarea name="zusammenfassung" value={form.zusammenfassung} onChange={handleChange} placeholder="Zusammenfassung des gesamten Prozesses..." style={{ minHeight: 120 }} />
        </div>
      </div>

      {/* Key Learnings */}
      <div className="block-form-section">
        <h4>Key Learnings</h4>
        <div className="form-group">
          <label>Die wichtigsten Erkenntnisse</label>
          <textarea name="key_learnings" value={form.key_learnings} onChange={handleChange} placeholder="Zentrale Erkenntnisse und Lerneffekte..." />
        </div>
      </div>

      {/* Transferplan */}
      <div className="block-form-section">
        <h4>Transferplan</h4>
        {form.transferplan.map((t, i) => (
          <div className="dynamic-list-item" key={i}>
            <input
              type="text"
              value={t.was}
              onChange={(e) => updateTransfer(i, 'was', e.target.value)}
              placeholder="Was? (Konkrete Aktion)"
              style={{ flex: 2 }}
            />
            <input
              type="text"
              value={t.wann}
              onChange={(e) => updateTransfer(i, 'wann', e.target.value)}
              placeholder="Wann? (Zeitrahmen)"
              style={{ flex: 1 }}
            />
            <input
              type="text"
              value={t.wie}
              onChange={(e) => updateTransfer(i, 'wie', e.target.value)}
              placeholder="Wie? (Strategie)"
              style={{ flex: 1 }}
            />
            <button className="btn-icon" onClick={() => removeTransfer(i)} title="Entfernen">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button className="btn btn-sm btn-secondary" onClick={addTransfer}>
          <Plus size={14} /> Aktion hinzufügen
        </button>
      </div>

      {/* Rückfallprävention */}
      <div className="block-form-section">
        <h4>Rückfallprävention</h4>
        <div className="form-group">
          <label>Was tun, wenn alte Muster zurückkehren? Strategien für schwierige Situationen</label>
          <textarea name="rueckfallpraevention" value={form.rueckfallpraevention} onChange={handleChange} placeholder="Strategien und Notfallplan..." />
        </div>
      </div>

      {/* Empfehlungen */}
      <div className="block-form-section">
        <h4>Empfehlungen des Coaches</h4>
        <div className="form-group">
          <label>Empfehlungen für die weitere Entwicklung</label>
          <textarea name="empfehlungen" value={form.empfehlungen} onChange={handleChange} placeholder="Weitere Empfehlungen..." />
        </div>
      </div>

      {/* Abschlussfeedback */}
      <div className="block-form-section">
        <h4>Abschlussfeedback</h4>
        <div className="form-group">
          <label>Gesamtbewertung</label>
          <div className="range-display">
            <input type="range" name="gesamtbewertung" min={1} max={10} value={form.gesamtbewertung} onChange={handleChange} style={{ flex: 1 }} />
            <span className="range-value">{form.gesamtbewertung}</span>
          </div>
        </div>
        <div className="form-group">
          <label>Kommentar</label>
          <textarea name="abschluss_kommentar" value={form.abschluss_kommentar} onChange={handleChange} placeholder="Abschließende Gedanken..." />
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={16} /> {saving ? 'Speichern...' : 'Speichern'}
        </button>
        <button className="btn btn-secondary" onClick={handleExport}>
          <Eye size={16} /> PDF-Vorschau
        </button>
        {saved && (
          <span className="save-indicator">
            <Check size={14} /> Gespeichert
          </span>
        )}
      </div>
    </div>
  );
}
